// packages/web/src/lib/auditionSpread.ts — A4: EVERY DERIVATION THE
// Project › Role › Audition SCREENS MAKE, IN ONE PLACE, AS PURE FUNCTIONS.
//
// The spec (spec-a4-project-role-audition-2026-08-19) says the same thing in
// four different places, so it is worth saying once, here, plainly:
//
//   THE PACK IS THE VOCABULARY. THIS FILE HOLDS NO COPY OF IT.
//
// There is no list of audition states in this file, no list of sides colours,
// no set of "terminal" state names. Every one of those lives in
// packages/verticals/actor-assist/src/recordTypes.ts and reaches the browser
// through GET /records/types/:key — `schema.lifecycle.states`,
// `schema.lifecycle.transitions` and a picklist field's `picklistValues`.
//
// WHY THAT IS A RULE AND NOT A PREFERENCE. `@vowric/web` does not depend on
// `@vowric/vertical-actor-assist` and must not start — a pack is server-side
// and its manifest has no business in a browser bundle. So the only two ways
// to know the pack's vocabulary here are (a) copy it, or (b) read what the
// server already serves. A copy is right the day it is written and quietly
// wrong the first time a stage is added; it would show a stepper missing a
// step, which looks like a working stepper. (b) it is.
//
// ===================================================================
// WHAT IS DELIBERATELY NOT HERE: PER-GATE DATES
// ===================================================================
// The work-order spread's rule is "the stepper shows WHEN each gate was
// passed, not just where we are" (S2 §1b). This pack cannot answer that yet.
// A record's lifecycle transitions are not retained anywhere the browser can
// reach: `records` carries one `updated_at`, and `audit_logs` has no read
// route. So this file computes NO per-gate dates and the stepper renders
// none — rather than reading `updated_at` as "in this state since", which any
// edit to any field would silently reset. A plausible wrong number on a
// stepper is worse than an absent one.
//
// What it computes instead is the audition's own AGE, from fields the pack
// actually declares (`due_at`, `submitted_at`). See `auditionAge`.
import type { LinkRow, RecordRow, RecordTypeSchema, SchemaLifecycle } from './api';
import { humanizeValue } from './fieldDisplay';

// -------------------------------------------------------------------
// 1. THE STEPPER SPINE — derived from the transition table, not named
// -------------------------------------------------------------------

export interface SpineShape {
  /** The main line, in the pack's own declared order. */
  spine: string[];
  /** Every declared state that is not on it — rendered only when the record
   * is actually sitting in one. */
  offSpine: string[];
}

/**
 * The advancement line through a lifecycle, WITHOUT knowing a single state's
 * name.
 *
 * THE DERIVATION, and why it is this one. Walk the pack's declared state
 * order greedily and keep a state only if it is reachable in ONE HOP from the
 * last state kept. On `aa_audition` that yields exactly
 * `submitted → auditioned → callback → chemistry_read → screen_test → pinned
 * → offered → booked` and leaves `passed` and `archived` off — which is the
 * hand-drawn answer, arrived at structurally.
 *
 * It works because a pack declares its states in the order they happen (the
 * engine matches transitions by exact {from,to} pair and has no wildcard, so
 * every edge is enumerated and the order is load-bearing documentation), and
 * because an exit like `passed` is reachable from every active stage but
 * never from the stage before it in declared order.
 *
 * A lifecycle whose declared order is NOT its advancement order degrades to a
 * short spine plus a long off-spine list — visibly odd rather than subtly
 * wrong, which is the failure mode to prefer.
 */
export function spineFromLifecycle(lifecycle: SchemaLifecycle | undefined): SpineShape {
  const states = lifecycle?.states ?? [];
  if (states.length === 0) return { spine: [], offSpine: [] };
  const transitions = lifecycle?.transitions ?? {};

  const first = states[0];
  if (first === undefined) return { spine: [], offSpine: [] };

  const spine: string[] = [first];
  for (const state of states.slice(1)) {
    const from = spine[spine.length - 1] ?? '';
    if ((transitions[from] ?? []).includes(state)) spine.push(state);
  }
  const onSpine = new Set(spine);
  return { spine, offSpine: states.filter((s) => !onSpine.has(s)) };
}

export interface Step {
  state: string;
  label: string;
  /** This step is at or behind where the record has got to. */
  reached: boolean;
  /** The record is sitting here right now. */
  current: boolean;
  /** Not part of the advancement line — inserted because the record is in it. */
  offSpine: boolean;
}

/**
 * The steps to render for one record, with an off-spine state inserted AT ITS
 * PLACE rather than appended or hidden.
 *
 * "At its place" means: after the last spine state the record can be shown to
 * have passed. We cannot know which stage a `passed` audition passed FROM —
 * that is the same missing history the file header describes — so an
 * off-spine current state is rendered at the END of the reached run, which is
 * the furthest position it could honestly occupy.
 *
 * A record whose state is not declared at all (a stale row after a manifest
 * change) renders the plain spine with nothing marked current. It does not
 * throw, and it does not invent a step.
 */
export function stepsFor(lifecycle: SchemaLifecycle | undefined, state: string | null): Step[] {
  const { spine, offSpine } = spineFromLifecycle(lifecycle);
  const label = (s: string) => humanizeValue(s);

  if (state && offSpine.includes(state)) {
    // Off-spine and current: the whole spine reads as not-reached (we do not
    // know how far it actually got), and the off-spine state is the terminus.
    return [
      ...spine.map((s) => ({ state: s, label: label(s), reached: false, current: false, offSpine: false })),
      { state, label: label(state), reached: true, current: true, offSpine: true },
    ];
  }

  const at = state ? spine.indexOf(state) : -1;
  return spine.map((s, i) => ({
    state: s,
    label: label(s),
    reached: at >= 0 && i <= at,
    current: i === at,
    offSpine: false,
  }));
}

// -------------------------------------------------------------------
// 2. THE AUDITION'S AGE — from fields the pack declares
// -------------------------------------------------------------------

export type AuditionAge =
  | { kind: 'due'; days: number }
  | { kind: 'overdue'; days: number }
  | { kind: 'out'; days: number }
  // B15 — once a tape has actually gone, the deadline stops being the
  // story; WHEN it went is. `at` is the moment itself, not a day count — the
  // words are "Sent Tue, Sep 25", not "sent 4 days ago". This file has no
  // day formatter of its own (that is `comingUpDayLabel`, lib/calendarItems.ts
  // — and that module already imports THIS one, so this file cannot import
  // it back), so `ageWords` below hands a 'sent' age back unworded and the
  // few callers that may see one format it themselves.
  | { kind: 'sent'; at: Date }
  | null;

const DAY_MS = 24 * 60 * 60 * 1000;

function parseWhen(v: unknown): Date | null {
  if (typeof v !== 'string' || !v.trim()) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Whole days between two instants, floored — 23 hours is still "today". */
function daysBetween(from: Date, to: Date): number {
  return Math.floor((to.getTime() - from.getTime()) / DAY_MS);
}

/**
 * What the number on the current step says.
 *
 * A self-tape has a DEADLINE and that is the urgent fact; an audition already
 * out has SILENCE and that is the whole story ("silence is the story" — the
 * spec's own words, and the reason the number sits on the current step at
 * all).
 *
 * Returns null when the pack has nothing to answer with. A null age renders
 * as no number, never as a zero.
 */
export function auditionAge(record: RecordRow, now: Date = new Date()): AuditionAge {
  // B15 — THE WALK'S OWN FINDING: an audition already `submitted` (or
  // further along) still read "due in 7 days" — a deadline that was MET,
  // reported back as one still ticking. `requested` is the pack's only
  // pre-submission stage (B13, decision `2d0f7662`), so any other state
  // means a tape actually went, and WHEN is the fact worth stating —
  // never a due date the record has already moved past.
  if (record.lifecycle_state && record.lifecycle_state !== 'requested') {
    const sentAt = parseWhen(record.fields.submitted_at);
    // No stamped date to report is honestly "we don't know" — never the
    // due date the record has already moved past.
    return sentAt ? { kind: 'sent', at: sentAt } : null;
  }
  const due = parseWhen(record.fields.due_at);
  if (due) {
    const days = daysBetween(now, due);
    return days >= 0 ? { kind: 'due', days } : { kind: 'overdue', days: -days };
  }
  const submitted = parseWhen(record.fields.submitted_at);
  if (submitted) {
    const days = daysBetween(submitted, now);
    // A submitted_at in the future is a typo, not a negative age.
    return days >= 0 ? { kind: 'out', days } : null;
  }
  return null;
}

export function ageWords(age: AuditionAge): string | null {
  if (!age) return null;
  const d = (n: number) => (n === 1 ? '1 day' : `${n} days`);
  switch (age.kind) {
    case 'due':
      return age.days === 0 ? 'due today' : `due in ${d(age.days)}`;
    case 'overdue':
      return age.days === 0 ? 'due today' : `overdue ${d(age.days)}`;
    case 'out':
      return age.days === 0 ? 'out today' : `out ${d(age.days)}`;
    // Unworded on purpose — see the AuditionAge type's own comment. A caller
    // that may see a 'sent' age formats it with `comingUpDayLabel`.
    case 'sent':
      return null;
  }
}

/** A human phrase for an audition with no role to name it by — never the
 * record's own `display_name`. `name_template` is `'{modality}'`
 * (recordTypes.ts) — the least-wrong single field this pack can put in a
 * template, since a role's name is not a field on this type and
 * renderNameTemplate has no way to humanize a picklist value — so a
 * self-tape audition's raw `display_name` is the unhumanized `self_tape`.
 * Every screen that falls back to this type's own name (B15's own finding:
 * HouseholdAuditions.tsx and calendarItems.ts's due-date row) reads it
 * through here instead. */
export function auditionFallbackName(record: RecordRow): string {
  const modality = record.fields.modality;
  return typeof modality === 'string' && modality ? humanizeValue(modality) : 'This read';
}

// -------------------------------------------------------------------
// 3. THE SIDES TRAIL — the role owns it, and "current" is derived
// -------------------------------------------------------------------

/**
 * The trail, newest first.
 *
 * The ordering rule is the pack's own (`currentSidesRevision` in
 * recordTypes.ts): latest `issued_at`, and where two revisions were issued at
 * the same instant, the later COLOUR in the industry's order wins. That order
 * is a picklist on the pack — pass it in from
 * `schema.fields.find(f => f.name === 'revision_colour').picklistValues`.
 *
 * `issued_at` is REQUIRED on aa_sides precisely so that a revision can always
 * be placed on the trail. A row that somehow has none sorts to the bottom
 * rather than to the top: an unplaceable revision must never become "current".
 */
export function sidesTrail(rows: RecordRow[], colourOrder: string[]): RecordRow[] {
  const colourIndex = (r: RecordRow) => {
    const c = r.fields.revision_colour;
    const i = typeof c === 'string' ? colourOrder.indexOf(c) : -1;
    return i;
  };
  return [...rows].sort((a, b) => {
    const ta = parseWhen(a.fields.issued_at)?.getTime();
    const tb = parseWhen(b.fields.issued_at)?.getTime();
    if (ta === undefined && tb === undefined) return colourIndex(b) - colourIndex(a);
    if (ta === undefined) return 1;
    if (tb === undefined) return -1;
    if (ta !== tb) return tb - ta;
    return colourIndex(b) - colourIndex(a);
  });
}

/** The head of the trail, or null. Never a stored flag — see the trail's own
 * ordering rule above. */
export function currentSides(rows: RecordRow[], colourOrder: string[]): RecordRow | null {
  const sorted = sidesTrail(rows, colourOrder);
  const placeable = sorted.filter((r) => parseWhen(r.fields.issued_at) !== null);
  return (placeable[0] ?? sorted[0]) ?? null;
}

export type SidesStanding =
  /** The audition points at no revision. Not the same as being on the current
   * one, and the screen must not assume the latest. */
  | { kind: 'unrecorded' }
  /** Reading from the head of the trail. */
  | { kind: 'current'; read: RecordRow }
  /** Reading from an older revision, and one has been issued since. */
  | { kind: 'behind'; read: RecordRow; current: RecordRow }
  /** There is no trail at all. */
  | { kind: 'no_trail' };

/**
 * Where one audition stands against the role's trail — the line that is the
 * whole reason the sides column exists.
 *
 * `readId` is `aa_audition.audition_sides`, a LOOKUP and never a
 * master_detail: it records which revision was read from, and the trail
 * survives this audition being archived.
 */
export function sidesStanding(
  trail: RecordRow[],
  colourOrder: string[],
  readId: string | null
): SidesStanding {
  if (trail.length === 0) return { kind: 'no_trail' };
  const current = currentSides(trail, colourOrder);
  if (!readId) return { kind: 'unrecorded' };
  const read = trail.find((r) => r.id === readId);
  if (!read) return { kind: 'unrecorded' };
  if (!current || current.id === read.id) return { kind: 'current', read };
  return { kind: 'behind', read, current };
}

/** "Pink — rev 3", or the row's own display_name when the pack's template
 * already said it better. Never a colour swatch: goldenrod on carbon is
 * unreadable, and the industry's WORD for it is the data. */
export function sidesLabel(row: RecordRow): string {
  const colour = row.fields.revision_colour;
  const version = row.fields.version_label;
  const parts = [
    typeof colour === 'string' && colour ? humanizeValue(colour) : null,
    typeof version === 'string' && version ? version : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : row.display_name;
}

/** The colour vocabulary, read off the pack's own picklist. Empty when the
 * schema has not loaded — every consumer here degrades to date-only ordering,
 * which is right rather than merely safe. */
export function colourOrderFrom(schema: RecordTypeSchema | null): string[] {
  return schema?.fields.find((f) => f.name === 'revision_colour')?.picklistValues ?? [];
}

/** P2 — the binder's re-type control offers exactly the classifier's own
 * `kind` vocabulary (aa_source, recordTypes.ts), read off its schema rather
 * than copied here — the same reason colourOrderFrom above reads
 * `revision_colour` instead of a hand-kept list. Empty while the schema has
 * not loaded; a caller degrades to "no options yet" rather than a wrong set. */
export function sourceKindOptionsFrom(schema: RecordTypeSchema | null): string[] {
  return schema?.fields.find((f) => f.name === 'kind')?.picklistValues ?? [];
}

// -------------------------------------------------------------------
// 4. THE SEAM — one performer's chronology across a role
// -------------------------------------------------------------------

export interface SeamEntry {
  kind: 'audition' | 'engagement';
  record: RecordRow;
  /** Sort key: when this thing started. Null sorts last. */
  when: Date | null;
}

export interface SeamBlock {
  subjectId: string;
  /** Filled by the caller from the performers it already loaded; the id is
   * all this module can know. */
  entries: SeamEntry[];
}

function seamWhen(kind: SeamEntry['kind'], r: RecordRow): Date | null {
  return kind === 'audition'
    ? parseWhen(r.fields.submitted_at) ?? parseWhen(r.fields.due_at)
    : parseWhen(r.fields.block_start) ?? parseWhen(r.fields.offered_at);
}

/**
 * ONE BLOCK PER PERFORMER, auditions and engagements in one chronology.
 *
 * This is decision 15155d2f made visible. Booking creates a seam — the tape
 * and the sides sit on a record that becomes historical while call sheets and
 * payments accrue on a new one — and the actor's mental model is continuous
 * across it. The Role is the record that spans the seam, so on the Role
 * screen the two kinds share one column and one chronology.
 *
 * WHY IT GROUPS BY SUBJECT AND THE CALLER DECIDES WHETHER TO SHOW HEADINGS.
 * Two siblings reading the same character is the case the whole pack was
 * reshaped around: one role, one trail, two auditions. But a role with ONE
 * performer must not render a heading for them — the same rule the household
 * Today follows, or the subject switcher the household home exists to avoid
 * grows back here.
 *
 * Blocks come back in the order the performers were first involved, so a
 * screen never has to sort them and two screens never disagree.
 */
export function seamBlocks(
  auditions: { record: RecordRow; subjectId: string | null }[],
  engagements: { record: RecordRow; subjectId: string | null }[]
): SeamBlock[] {
  const byId = new Map<string, SeamEntry[]>();
  const push = (subjectId: string | null, entry: SeamEntry) => {
    // A record with no performer link is not dropped: it lands under the
    // empty key so the screen can still show it. Silently losing an audition
    // because its subject link is missing is exactly the failure that reads
    // as "she has no auditions".
    const key = subjectId ?? '';
    const list = byId.get(key);
    if (list) list.push(entry);
    else byId.set(key, [entry]);
  };
  for (const a of auditions) push(a.subjectId, { kind: 'audition', record: a.record, when: seamWhen('audition', a.record) });
  for (const e of engagements)
    push(e.subjectId, { kind: 'engagement', record: e.record, when: seamWhen('engagement', e.record) });

  const blocks: SeamBlock[] = [];
  for (const [subjectId, entries] of byId) {
    entries.sort((x, y) => {
      if (x.when && y.when) return x.when.getTime() - y.when.getTime();
      if (x.when) return -1;
      if (y.when) return 1;
      return 0;
    });
    blocks.push({ subjectId, entries });
  }
  blocks.sort((a, b) => {
    const ta = a.entries[0]?.when?.getTime() ?? Number.MAX_SAFE_INTEGER;
    const tb = b.entries[0]?.when?.getTime() ?? Number.MAX_SAFE_INTEGER;
    return ta - tb;
  });
  return blocks;
}

// -------------------------------------------------------------------
// 5. THE PROJECT LENS — derived, because aa_project has no lifecycle
// -------------------------------------------------------------------

export type ProjectLens = 'all' | 'in_play' | 'booked';

export const PROJECT_LENSES: { id: ProjectLens; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'in_play', label: 'In play' },
  { id: 'booked', label: 'Booked' },
];

/**
 * WHY THIS IS DERIVED AND NOT A FIELD.
 *
 * The July spec gave the old Actor pack a project PHASE — Auditioned /
 * Booked / Released — with its own lifecycle. `aa_project` has neither: no
 * lifecycle, no phase field, and `released` has no representation anywhere in
 * this pack. Rendering the old strip here would filter on states that do not
 * exist.
 *
 * Adding one is a MANIFEST CHANGE, and the manifest is immutable per version
 * (migration 0033) — amending it means bumping `authority.version`, which is
 * a decision with a migration, not a strip. So the strip is derived from what
 * the pack does say: a project is `booked` if any read on it has reached the
 * lifecycle's success exit or it has an engagement, `in_play` if any read on
 * it is still live.
 *
 * `spine` is the derived advancement line (see spineFromLifecycle): its LAST
 * entry is the success exit and everything before it is a live stage. No
 * state name appears in this function.
 */
export function projectLens(
  spine: string[],
  auditionStates: (string | null)[],
  engagementCount: number
): Exclude<ProjectLens, 'all'> | null {
  const booked = spine.length > 0 ? spine[spine.length - 1] : null;
  const live = new Set(spine.slice(0, -1));
  if (engagementCount > 0) return 'booked';
  if (booked && auditionStates.some((s) => s === booked)) return 'booked';
  if (auditionStates.some((s) => s !== null && live.has(s))) return 'in_play';
  return null;
}

export function matchesLens(lens: ProjectLens, derived: ReturnType<typeof projectLens>): boolean {
  return lens === 'all' || lens === derived;
}

// -------------------------------------------------------------------
// 6. LINK PLUMBING — the pack's own relationship keys, in one place
// -------------------------------------------------------------------

/** The relationship keys these four screens traverse. Named constants rather
 * than string literals scattered through JSX, so a rename in the pack is one
 * grep with one answer. */
export const AA = {
  PROJECT: 'aa_project',
  ROLE: 'aa_role',
  SIDES: 'aa_sides',
  AUDITION: 'aa_audition',
  SESSION: 'aa_session',
  ENGAGEMENT: 'aa_engagement',
  SUBJECT: 'aa_subject',
  PERSON: 'aa_person',
  // A5 — the far side of the seam.
  SHOOT_DAY: 'aa_shoot_day',
  // A6 — the craft.
  // W2 — the position somebody held on a production.
  CREW_CREDIT: 'aa_crew_credit',
  MEDIA: 'aa_media',
  REVIEW: 'aa_review',
  PROFILE: 'aa_profile',
  PAYMENT: 'aa_payment',
  EXPENSE: 'aa_expense',
  DOCUMENT: 'aa_document',
  // W5 — the resume blocks. The three types in this pack that hold nothing
  // but what a person typed: a class they took, a prize they won, a thing
  // they can do. Nothing derives them and nothing ever will, which is why
  // they are the ONLY part of the resume with an add door on it.
  TRAINING: 'aa_training',
  AWARD: 'aa_award',
  SKILL: 'aa_skill',
  // W3 — a resume the person SHAPED, saved so it can be sent again. The one
  // type on the resume path a plan gates (`resume.editor`, Working+); the
  // three above it are free on every tier and always will be.
  RESUME_VERSION: 'aa_resume_version',
  // P2 — the project binder reads THIS type's own picklist (`kind`) for its
  // re-type control; see sourceKindOptionsFrom below.
  SOURCE: 'aa_source',
} as const;

export const LINK = {
  /** aa_role → aa_project (master_detail, unprefixed: the single owner). */
  ROLE_PROJECT: 'project',
  /** aa_project → aa_project, anthology seasons as separate linked productions. */
  ANTHOLOGY_PARENT: 'project_anthology_parent',
  /** aa_sides → aa_role (master_detail). The trail's home. */
  SIDES_ROLE: 'sides_role',
  /** aa_audition → aa_role (master_detail). */
  AUDITION_ROLE: 'role',
  /** aa_audition → aa_subject. THE AXIS — the one unprefixed subject hop. */
  AUDITION_SUBJECT: 'subject',
  /** aa_audition → aa_sides (LOOKUP — which revision was read from). */
  AUDITION_SIDES: 'audition_sides',
  /** aa_audition → aa_person. WHICH OFFICE — cardinality ONE, the single
   * authoritative one. */
  AUDITION_CASTING_OFFICE: 'audition_casting_office',
  /** aa_audition → aa_person, cardinality MANY. Everyone the breakdown NAMED
   * on the casting/production side (decision `deeb6266`). A different question
   * from `audition_casting_office`: that one is which office this read belongs
   * to; this is who was in the room. A breakdown routinely names three or
   * four. */
  AUDITION_PEOPLE: 'people',
  /** aa_session → aa_audition (master_detail). UNPREFIXED — `audition`, not
   * `session_audition`. A master_detail owner keeps the BARE name in this
   * pack (`aa_role`'s owner is `project`, `aa_audition`'s is `role`), and
   * this constant read `session_audition` from the day A4 shipped until
   * 2026-08-19.
   *
   * NOTHING CAUGHT IT, AND THE REASON IS WORTH KEEPING: the A4 walk never
   * created a session, so the spread's visits region was only ever asserted
   * EMPTY — which is exactly what a wrong key renders. A wrong key and an
   * empty list are indistinguishable from the outside, so the only test that
   * can tell them apart is one that puts a session there. See
   * packLinkKeys.test.ts, which now checks EVERY entry in this object
   * against the pack's own declarations rather than one at a time. */
  SESSION_AUDITION: 'audition',
  /** aa_engagement → aa_role (master_detail). Prefixed — the pack declares it
   * that way, and it is not an inconsistency to tidy: `engagement_role` is
   * what recordTypes.ts says, so it is what goes here. THIS OBJECT MIRRORS
   * THE PACK; IT DOES NOT REGULARISE IT. */
  ENGAGEMENT_ROLE: 'engagement_role',
  /** aa_engagement → aa_audition (LOOKUP, OPTIONAL BY DESIGN — a direct
   * offer for a returning character has no audition at all). */
  ENGAGEMENT_AUDITION: 'engagement_audition',
  /** aa_engagement → aa_subject. */
  ENGAGEMENT_SUBJECT: 'engagement_subject',
  /** aa_engagement → aa_sides (LOOKUP). The SAME trail the audition read
   * from, reached from the far side of the seam. */
  ENGAGEMENT_SIDES: 'engagement_sides',
  /** aa_shoot_day → aa_engagement (master_detail). */
  SHOOT_DAY_ENGAGEMENT: 'shoot_day_engagement',
  /** aa_payment → aa_engagement (master_detail). */
  PAYMENT_ENGAGEMENT: 'payment_engagement',
  /** aa_payment → aa_subject (LOOKUP). */
  PAYMENT_SUBJECT: 'payment_subject',
  /** aa_expense → aa_subject (LOOKUP, NULLABLE BY DESIGN — an absent link
   * means "the household's", never "unknown"). */
  EXPENSE_SUBJECT: 'expense_subject',
  /** aa_document → aa_engagement (LOOKUP). */
  DOCUMENT_ENGAGEMENT: 'document_engagement',
  /** aa_document → aa_subject (LOOKUP). */
  DOCUMENT_SUBJECT: 'document_subject',

  // ---- A6: the self-tape ----
  /** aa_media → aa_subject. ABOUTNESS — whose career this tape is about, and
   * the ONLY media pointer the read scope walks. */
  MEDIA_SUBJECT: 'media_subject',
  /** aa_media → aa_subject. A FAVOUR — which sibling read off camera. NOT
   * aboutness: a helper who read for her sister does not become the subject
   * of her sister's tape, and the pack's index.ts declares only
   * `media_subject` in `relations` for exactly that reason. */
  MEDIA_READER_SUBJECT: 'media_reader_subject',
  /** aa_media → aa_person — an outside reader. The pack keeps both because
   * both happen and "neither is more authoritative". */
  MEDIA_READER: 'media_reader',
  /** aa_media → aa_role. */
  MEDIA_ROLE: 'media_role',
  /** aa_media → aa_audition. */
  MEDIA_AUDITION: 'media_audition',
  /** aa_review → aa_media (master_detail). PREFIXED — unlike aa_role's
   * `project` and aa_session's `audition`, this pack's later types name their
   * owner in full. Mirror the pack; do not regularise it. */
  REVIEW_MEDIA: 'review_media',
  /** aa_review → aa_subject. */
  REVIEW_SUBJECT: 'review_subject',
  /** aa_review → aa_person. */
  REVIEW_REVIEWER: 'review_reviewer',

  /** aa_profile → aa_subject. WHOSE profile this is. There is ONE per
   * performer by the pack's own convention, so this is what lets a
   * household-wide read of the profiles answer "every performer's face" in
   * one pass instead of one read per person. */
  PROFILE_SUBJECT: 'profile_subject',
  /** aa_profile → aa_media. THE FACE THE HOUSEHOLD ALREADY CHOSE — what a
   * saved resume version prints when it names no headshot of its own (W3).
   * A LOOKUP: the profile points at a photograph, it does not own one. */
  PROFILE_CURRENT_HEADSHOT: 'profile_current_headshot',

  // ---- W5: the resume blocks ----
  /** aa_training → aa_subject. ABOUTNESS, and the reason a household with
   * two performers does not print one merged CV: the pack's own comment on
   * these three types says exactly that. Each is PREFIXED — this pack's
   * later types name their owner in full (see REVIEW_MEDIA). */
  TRAINING_SUBJECT: 'training_subject',
  /** aa_award → aa_subject. */
  AWARD_SUBJECT: 'award_subject',
  /** aa_skill → aa_subject. */
  SKILL_SUBJECT: 'skill_subject',

  // ---- W3: the saved resume versions ----
  /** aa_resume_version → aa_subject. ABOUTNESS — whose resume this is an
   * arrangement OF, and the reason a household's two performers keep two
   * separate shelves of versions. */
  RESUME_VERSION_SUBJECT: 'resume_version_subject',
  /** aa_resume_version → aa_media. THE FACE THAT GOES WITH THIS ONE, which
   * is half of what the type exists for: a theatre submission and a
   * commercial submission are two different photographs.
   *
   * ⚠ ABSENT MEANS THE PROFILE'S CURRENT HEADSHOT, never "no photo". A
   * version saved before anyone thought about the picture prints the face
   * the household already chose (aa_profile's `profile_current_headshot`). */
  RESUME_VERSION_HEADSHOT: 'resume_version_headshot',

  // ---- P2: the project binder ----
  /** aa_source → aa_project (LOOKUP). What Filing writes on every tier — a
   * capture already classifies and files itself here; the binder (Pro) adds
   * MANAGEMENT over the same link, never a second copy of it. */
  SOURCE_PROJECT: 'source_project',

  // ---- W2: the credit, and its two ends ----
  /** aa_crew_credit → aa_person (LOOKUP, cardinality one). */
  CREDIT_PERSON: 'crew_credit_person',
  /** aa_crew_credit → aa_project (LOOKUP, cardinality one).
   *
   * ⚠ PREFIXED, AND THE W2 SPEC SAID `project`. Core's C2a collision scan
   * refused the bare name at seed time — `project` is already aa_role's
   * master_detail owner, and `links` rows carry no src_type in the
   * (tenant, dst_type, dst_id, link_type) key, so any read scoped to that
   * relationship key answers for BOTH source types at once. The engine's own
   * warning suggested `crew_credit_project`; that is the name. Mirroring the
   * pack, as this whole object does. */
  CREDIT_PROJECT: 'crew_credit_project',
} as const;

// -------------------------------------------------------------------
// 7. THE PEOPLE IN THE ROOM — how many times this household has read
//    for the same person (decision `deeb6266`)
// -------------------------------------------------------------------

export interface PersonStanding {
  /** How many of this household's auditions name this person. */
  reads: number;
  /** WHICH of those this audition is, counted oldest-first — so a person on
   * their third read reads "3rd", not "3 reads" with no idea where you are.
   * 0 when this audition is not among them (which should not happen, and
   * renders as nothing rather than as a wrong number). */
  ordinal: number;
}

/**
 * How many reads this household has had with one person, and which one THIS
 * audition is.
 *
 * ⚠ STRICTLY THIS HOUSEHOLD'S, AND THAT IS THE RULING (decision `deeb6266`).
 * The input is the tenant's own auditions, fetched through the ordinary
 * enforced list — there is no cross-tenant number available here and there
 * must never be one. "3rd audition with this casting director" is a fact about
 * a family, not a reputation about a stranger.
 *
 * ORDERED BY WHEN THE READ WENT OUT (`submitted_at`), falling back to when the
 * record was created — because a breakdown captured today for a read submitted
 * last month is still last month's read. An audition with neither sorts last,
 * which is the only honest place for "we do not know when".
 */
export function personStanding(
  auditions: RecordRow[],
  linksByRecordId: Record<string, LinkRow[]>,
  personId: string,
  thisAuditionId: string
): PersonStanding {
  const theirs = auditions.filter((a) => inRead(linksByRecordId[a.id] ?? [], a.id, personId));
  const sortKey = (a: RecordRow): number => {
    const raw = typeof a.fields.submitted_at === 'string' ? a.fields.submitted_at : a.created_at;
    const t = raw ? Date.parse(raw) : Number.NaN;
    return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
  };
  theirs.sort((a, b) => sortKey(a) - sortKey(b));
  const at = theirs.findIndex((a) => a.id === thisAuditionId);
  return { reads: theirs.length, ordinal: at < 0 ? 0 : at + 1 };
}

/** "1st", "2nd", "3rd", "11th"… — English ordinals, including the teens the
 * naive rule gets wrong. */
export function ordinalWord(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

/** The other end of `linkType` from `selfId`, or null. Direction-agnostic:
 * core's listLinks matches src_id OR dst_id, so a record's link set already
 * contains everything pointing AT it. */
export function otherEnd(links: LinkRow[], selfId: string, linkType: string): string | null {
  for (const l of links) {
    if (l.link_type !== linkType) continue;
    if (l.src_id === selfId) return l.dst_id;
    if (l.dst_id === selfId) return l.src_id;
  }
  return null;
}

/** Every other end — for the many side of a master_detail, which is how a
 * role reaches its auditions and a project reaches its roles. */
/**
 * Was this person part of this read, in either capacity?
 *
 * A read names people two ways: `people` (everyone the breakdown named — who
 * was in the room) and `audition_casting_office` (the single office it belongs
 * to). Asking only the first hid every casting director from her own page:
 * the spread would say "read for Ilse Brandt" and count CASTING OFFICE 1 while
 * her page answered "No reads name them yet." — the same record, two screens,
 * opposite answers. "How many times have we read with this person" does not
 * care which end of the room they stood at.
 */
export function inRead(links: LinkRow[], auditionId: string, personId: string): boolean {
  return (
    otherEnds(links, auditionId, LINK.AUDITION_PEOPLE).includes(personId) ||
    otherEnds(links, auditionId, LINK.AUDITION_CASTING_OFFICE).includes(personId)
  );
}

export function otherEnds(links: LinkRow[], selfId: string, linkType: string): string[] {
  const out: string[] = [];
  for (const l of links) {
    if (l.link_type !== linkType) continue;
    if (l.src_id === selfId) out.push(l.dst_id);
    else if (l.dst_id === selfId) out.push(l.src_id);
  }
  return out;
}

/** Season/episode as one readable reference, or null when the pack has
 * neither. `episode` is TEXT on purpose — real call sheets say "101", "1-3"
 * and "204/205" — so it is rendered verbatim, never parsed. */
export function seasonEpisode(fields: Record<string, unknown>): string | null {
  const season = fields.season;
  const episode = fields.episode;
  const s = typeof season === 'number' && Number.isFinite(season) ? `S${season}` : null;
  const e = typeof episode === 'string' && episode.trim() ? `E${episode.trim()}` : null;
  if (!s && !e) return null;
  return [s, e].filter(Boolean).join(' ');
}

/** An engagement's season/episode-count, as a job listing reads it — never
 * the record's own `display_name`. `aa_engagement`'s name_template is
 * `Season {season} — {episode_count} ep`, a correct record name and a
 * broken headline the moment either half is empty: a job with a season and
 * no count read "Season 1 — ep", the template's placeholder left with
 * nothing to fill it (B19, the walk's own finding). Every clause is
 * conditional, joined, never prefixed — the same rule `seasonEpisode` above
 * and `AuditionSpread`'s sub-line already follow. Null when the engagement
 * carries neither field. */
export function engagementSeasonLine(fields: Record<string, unknown>): string | null {
  const season = fields.season;
  const episodeCount = fields.episode_count;
  const s = typeof season === 'number' && Number.isFinite(season) ? `Season ${season}` : null;
  const e =
    typeof episodeCount === 'number' && Number.isFinite(episodeCount) && episodeCount > 0
      ? episodeCount === 1
        ? '1 episode'
        : `${episodeCount} episodes`
      : null;
  if (!s && !e) return null;
  return [s, e].filter(Boolean).join(' · ');
}

// -------------------------------------------------------------------
// 8. THE PEOPLE SCREEN — a person, and what they have done with us
//    (W2, decision `9753e5ed`)
// -------------------------------------------------------------------

/** One `aa_crew_credit` with both of its ends already resolved, so no screen
 * walks the link set twice and no two screens disagree about which end is
 * which. Either end may be null: a credit read off a call sheet whose
 * production the model could not place is still a real position somebody
 * held, and dropping it would lose the only record of it. */
export interface CreditEnd {
  credit: RecordRow;
  personId: string | null;
  projectId: string | null;
}

/**
 * Resolve every credit's two ends, NEWEST FIRST.
 *
 * `created_at` is the sort key rather than any field on the credit, and that
 * is a deliberate limitation stated rather than hidden: a crew credit records
 * WHAT somebody did, not WHEN — a call sheet dates the shoot day, not the
 * job title beside a name. So "latest" here means "most recently learned",
 * which is the only ordering the data actually supports. A `worked_on` date
 * invented to sort by would be a fact the paperwork never stated.
 */
export function creditEnds(records: RecordRow[], linksByRecordId: Record<string, LinkRow[]>): CreditEnd[] {
  return [...records]
    .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
    .map((credit) => ({
      credit,
      personId: otherEnd(linksByRecordId[credit.id] ?? [], credit.id, LINK.CREDIT_PERSON),
      projectId: otherEnd(linksByRecordId[credit.id] ?? [], credit.id, LINK.CREDIT_PROJECT),
    }));
}

/** Every credit belonging to one person, in `creditEnds`' order. */
export function creditsForPerson(ends: CreditEnd[], personId: string): CreditEnd[] {
  return ends.filter((e) => e.personId === personId);
}

/** The verbatim position on a credit, or null. Rendered EXACTLY as it was
 * captured — never humanized, never title-cased: "1st AD" is what the sheet
 * printed and "1St Ad" is what a well-meaning formatter would make of it. */
export function positionOf(credit: RecordRow): string | null {
  const p = credit.fields.position;
  return typeof p === 'string' && p.trim() ? p.trim() : null;
}

/**
 * How many of this household's auditions name one person.
 *
 * ⚠ THIS HOUSEHOLD'S, AND ONLY EVER (decision `deeb6266`). The input is the
 * tenant's own audition set read through the ordinary enforced list; there is
 * no cross-family number available here and there must never be one. Shares
 * its rule with `personStanding` above, which answers the same question about
 * ONE audition — this is the count for a person with no audition in hand.
 */
export function readsWithPerson(
  auditions: RecordRow[],
  linksByRecordId: Record<string, LinkRow[]>,
  personId: string
): number {
  return auditions.filter((a) => inRead(linksByRecordId[a.id] ?? [], a.id, personId)).length;
}
